import { gql } from '@apollo/client';

// Hand-written against the backend surface — there is no FE codegen, so a drifted field name fails
// only at runtime (it arrives `undefined`), never at compile time.

const RULE_SET_VERSION_FIELDS = `
  id
  family
  version
  status
  parentVersion
  restoredFromVersion
  authorId
  authorName
  publishedAt
  artifactKey
  artifactPublishedAt
  createdAt
  updatedAt
`;

const RULE_FIELDS = `
  id
  ruleSetVersionId
  ruleId
  ordinal
  type
  ruleText
  severity
  enabled
  uiVisible
  section
  logic
  conditions
  evidenceFields
  severityOverrides
  displayDirective
  note
  source
  createdAt
  updatedAt
`;

// color-coding-rules-ui — the three child arrays (`ColorScoringRuleDto`/`ColorGateDto`/
// `ColorBandDto`), resolved by the SAME `include` as `rules` above (no per-row `@ResolveField`).
const COLOR_SCORING_RULE_FIELDS = `
  id
  ruleSetVersionId
  ruleId
  ordinal
  type
  ruleText
  enabled
  field
  points
  cases
  note
  source
  createdAt
  updatedAt
`;

const COLOR_GATE_FIELDS = `
  id
  ruleSetVersionId
  gateId
  ordinal
  kind
  order
  color
  logic
  ruleText
  conditions
  note
  source
  createdAt
  updatedAt
`;

const COLOR_BAND_FIELDS = `
  id
  ruleSetVersionId
  ordinal
  min
  max
  color
  createdAt
  updatedAt
`;

// 03-matching (SAA-172/SAA-193) — resolved by the SAME `include` as `rules`/the colour trio above.
const MATCHING_RULE_FIELDS = `
  id
  ruleSetVersionId
  ruleId
  ordinal
  kind
  type
  name
  ruleText
  weight
  enabled
  pairsWith
  sources
  definitions
  grades
  note
  source
  createdAt
  updatedAt
`;

// `cases` is a typed field resolver over the same `payload.cases`; it exists for `skipped` alone,
// the one part of a case that needs an enum — `RuleDryRunFieldResolver` normalises it by family, so
// it is the single path for BOTH families now.
const RULE_DRY_RUN_FIELDS = `
  dryRunId
  family
  mode
  status
  requestedById
  payload
  cases {
    caseId
    label
    status
    skipped {
      ruleId
      reason
      detail
      name
    }
  }
  suggestions
  failureReason
  requestedAt
  completedAt
`;

export const RULES_FAMILY_SUMMARY_GET_MANY = gql`
  query rulesFamilySummaryGetMany {
    rulesFamilySummaryGetMany {
      family
      publishedVersion
      publishedAt
      draftVersion
      ruleCount
      editable
    }
  }
`;

export const RULE_SET_VERSION_GET_MANY = gql`
  query ruleSetVersionGetMany($family: RuleFamily!) {
    ruleSetVersionGetMany(family: $family) {
      ${RULE_SET_VERSION_FIELDS}
    }
  }
`;

// There is no `ruleGetMany`: the rule list is always read through its parent version.
export const RULE_SET_VERSION_GET_ONE = gql`
  query ruleSetVersionGetOne($id: Int!) {
    ruleSetVersionGetOne(id: $id) {
      ${RULE_SET_VERSION_FIELDS}
      rules {
        ${RULE_FIELDS}
      }
      colorScoringRules {
        ${COLOR_SCORING_RULE_FIELDS}
      }
      colorGates {
        ${COLOR_GATE_FIELDS}
      }
      colorBands {
        ${COLOR_BAND_FIELDS}
      }
      matchingRules {
        ${MATCHING_RULE_FIELDS}
      }
    }
  }
`;

export const RULE_GET_ONE = gql`
  query ruleGetOne($id: Int!) {
    ruleGetOne(id: $id) {
      ${RULE_FIELDS}
    }
  }
`;

// The editor's field picker and the artifact the engine evaluates read the SAME rows; `operators`
// ships the legal subset per field, so the editor never re-derives the matrix.
export const FIELD_CATALOGUE_GET_MANY = gql`
  query fieldCatalogueGetMany($family: RuleFamily!) {
    fieldCatalogueGetMany(family: $family) {
      path
      label
      type
      appliesTo
      derived
      operators
      entity
      options {
        key
        label
      }
    }
  }
`;

// DELETES the row server-side on a terminal read — never call it twice for one result.
export const RULES_DRY_RUN_GET_ONE = gql`
  query rulesDryRunGetOne($dryRunId: String!) {
    rulesDryRunGetOne(dryRunId: $dryRunId) {
      ${RULE_DRY_RUN_FIELDS}
    }
  }
`;

export const RULE_SET_VERSION_CREATE_DRAFT = gql`
  mutation ruleSetVersionCreateDraft($family: RuleFamily!) {
    ruleSetVersionCreateDraft(family: $family) {
      ${RULE_SET_VERSION_FIELDS}
      rules {
        ${RULE_FIELDS}
      }
      colorScoringRules {
        ${COLOR_SCORING_RULE_FIELDS}
      }
      colorGates {
        ${COLOR_GATE_FIELDS}
      }
      colorBands {
        ${COLOR_BAND_FIELDS}
      }
      matchingRules {
        ${MATCHING_RULE_FIELDS}
      }
    }
  }
`;

// ONE mutation for create and edit: an input without `id` creates, and the rule id is allocated
// server-side.
export const RULE_UPSERT_ONE = gql`
  mutation ruleUpsertOne($input: RuleUpsertInput!) {
    ruleUpsertOne(input: $input) {
      ${RULE_FIELDS}
    }
  }
`;

// One call: validate, write the artifact, flip the version's status.
export const RULES_PUBLISH = gql`
  mutation rulesPublish($input: RulesPublishInput!) {
    rulesPublish(input: $input) {
      ${RULE_SET_VERSION_FIELDS}
    }
  }
`;

export const RULE_SET_VERSION_DISCARD_DRAFT = gql`
  mutation ruleSetVersionDiscardDraft($input: IdInput!) {
    ruleSetVersionDiscardDraft(input: $input)
  }
`;

export const RULES_DRY_RUN_START = gql`
  mutation rulesDryRunStart($input: RulesDryRunStartInput!) {
    rulesDryRunStart(input: $input) {
      ${RULE_DRY_RUN_FIELDS}
    }
  }
`;

// One rule, one profile; the returned `dryRunId` is polled with `RULES_DRY_RUN_GET_ONE`, exactly
// like the bench.
export const RULE_TEST_RUN = gql`
  mutation ruleTestRun($input: RuleTestRunInput!) {
    ruleTestRun(input: $input) {
      ${RULE_DRY_RUN_FIELDS}
    }
  }
`;

// `family` is required backend-side (P1-9/P1-8's `rulesImportCatalogue(family: RuleFamily!)`).
export const RULES_IMPORT_CATALOGUE = gql`
  mutation rulesImportCatalogue($family: RuleFamily!) {
    rulesImportCatalogue(family: $family)
  }
`;

// Draft-only.
export const RULE_DELETE_ONE = gql`
  mutation ruleDeleteOne($input: IdInput!) {
    ruleDeleteOne(input: $input)
  }
`;

// Loads a published version's rules into the draft: history is append-only, so a rollback ships as
// a new publish.
export const RULE_SET_VERSION_RESTORE_FROM_VERSION = gql`
  mutation ruleSetVersionRestoreFromVersion($input: RuleSetVersionRestoreInput!) {
    ruleSetVersionRestoreFromVersion(input: $input) {
      ${RULE_SET_VERSION_FIELDS}
      rules {
        ${RULE_FIELDS}
      }
      colorScoringRules {
        ${COLOR_SCORING_RULE_FIELDS}
      }
      colorGates {
        ${COLOR_GATE_FIELDS}
      }
      colorBands {
        ${COLOR_BAND_FIELDS}
      }
      matchingRules {
        ${MATCHING_RULE_FIELDS}
      }
    }
  }
`;

// ── Colour coding editor ops (PLAN §13) ──────────────────────────────────────────────────────────
export const COLOR_SCORING_RULE_UPSERT_ONE = gql`
  mutation colorScoringRuleUpsertOne($input: ColorScoringRuleUpsertInput!) {
    colorScoringRuleUpsertOne(input: $input) {
      ${COLOR_SCORING_RULE_FIELDS}
    }
  }
`;

export const COLOR_SCORING_RULE_DELETE_ONE = gql`
  mutation colorScoringRuleDeleteOne($input: IdInput!) {
    colorScoringRuleDeleteOne(input: $input)
  }
`;

export const COLOR_GATE_UPSERT_ONE = gql`
  mutation colorGateUpsertOne($input: ColorGateUpsertInput!) {
    colorGateUpsertOne(input: $input) {
      ${COLOR_GATE_FIELDS}
    }
  }
`;

export const COLOR_GATE_DELETE_ONE = gql`
  mutation colorGateDeleteOne($input: IdInput!) {
    colorGateDeleteOne(input: $input)
  }
`;

// Bands are a SET — replace-all is the honest op (PLAN §13); D4's exhaustive/non-overlap check runs
// at PUBLISH, never here.
export const COLOR_BANDS_REPLACE = gql`
  mutation colorBandsReplace($input: ColorBandsReplaceInput!) {
    colorBandsReplace(input: $input) {
      ${COLOR_BAND_FIELDS}
    }
  }
`;

// PLAN P11 — the derived reachable range + D4's blocking findings + D11/PG2's warnings, read BEFORE
// the publish click. `rulesPublish` independently re-runs the SAME validator.
export const COLOR_RUBRIC_PREVIEW_GET_ONE = gql`
  query colorRubricPreviewGetOne($ruleSetVersionId: Int!) {
    colorRubricPreviewGetOne(ruleSetVersionId: $ruleSetVersionId) {
      minScore
      maxScore
      blocking
      warnings
    }
  }
`;

// ── Matching editor ops (03-matching PLAN P1-14/P15) ─────────────────────────────────────────────
export const MATCHING_RULE_UPSERT = gql`
  mutation matchingRuleUpsert($input: MatchingRuleUpsertInput!) {
    matchingRuleUpsert(input: $input) {
      ${MATCHING_RULE_FIELDS}
    }
  }
`;

export const MATCHING_RULE_DELETE_ONE = gql`
  mutation matchingRuleDeleteOne($input: IdInput!) {
    matchingRuleDeleteOne(input: $input)
  }
`;

export const MATCHING_RULES_BULK_DELETE = gql`
  mutation matchingRulesBulkDelete($input: MatchingRulesBulkDeleteInput!) {
    matchingRulesBulkDelete(input: $input)
  }
`;

// PLAN P15 — the weights total and the V-1 WARN, read BEFORE the publish click.
export const MATCHING_RULES_PREVIEW_GET_ONE = gql`
  query matchingRulesPreviewGetOne($ruleSetVersionId: Int!) {
    matchingRulesPreviewGetOne(ruleSetVersionId: $ruleSetVersionId) {
      activeWeightTotal
      blocking
      warnings
    }
  }
`;
